import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Trash2, UploadCloud, FileText, Search, Pencil } from "lucide-react";
import { toast } from "sonner";
import { uploadFile } from "./MediaAdmin";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const inputCls = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-dwc-charcoal outline-none focus:border-dwc-green";

export const VisitorInsights = ({ token }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/visits/insights`, auth(token)).then((r) => setD(r.data)).catch(() => {});
  }, [token]);
  if (!d) return <p className="mt-8 text-sm font-semibold text-dwc-charcoal/50">Loading visitor insights...</p>;
  const cards = [["Today", d.totals.today], ["This Week", d.totals.this_week], ["This Month", d.totals.this_month], ["All Time", d.totals.all_time]];
  const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return (
    <div className="mt-6 space-y-5" data-testid="visitor-insights-panel">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map(([label, v]) => (
          <div key={label} className="rounded-2xl border border-gray-100 bg-white p-5 text-center" data-testid={`insight-card-${label.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-3xl font-black text-dwc-green">{v}</p>
            <p className="mt-1 text-[10px] font-extrabold uppercase tracking-wide text-dwc-charcoal/50">{label} · Unique Visitors</p>
          </div>
        ))}
      </div>
      <TrendChart title="Daily Unique Visitors — Last 14 Days" testId="daily-trend-chart" bars={d.daily.map((x) => ({ label: fmt(x.date), value: x.unique, hint: `${x.date}: ${x.unique} unique · ${x.visits} visits` }))} />
      <TrendChart title="Weekly Unique Visitors — Last 8 Weeks" testId="weekly-trend-chart" bars={d.weekly.map((x) => ({ label: `Wk of ${fmt(x.week_start)}`, value: x.unique, hint: `Week starting ${x.week_start}: ${x.unique} unique visitors` }))} />
      <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white p-5" data-testid="visitor-ip-table">
        <h3 className="text-xs font-extrabold uppercase tracking-wide text-dwc-charcoal/60">Recent Visitors — IP &amp; Location (last 50)</h3>
        <table className="mt-4 w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-[10px] font-extrabold uppercase tracking-wide text-dwc-charcoal/45">
              <th className="py-2 pr-4">IP Address</th><th className="py-2 pr-4">Location</th><th className="py-2 pr-4">Visits</th><th className="py-2 pr-4">First Seen</th><th className="py-2">Last Seen</th>
            </tr>
          </thead>
          <tbody>
            {d.visitors.map((v) => (
              <tr key={v.ip} className="border-b border-gray-50" data-testid={`visitor-row-${v.ip}`}>
                <td className="py-2.5 pr-4 font-mono text-xs font-bold text-dwc-charcoal">{v.ip}</td>
                <td className="py-2.5 pr-4 font-semibold text-dwc-charcoal/80">{v.location}</td>
                <td className="py-2.5 pr-4 font-bold text-dwc-green">{v.visits}</td>
                <td className="py-2.5 pr-4 text-xs text-dwc-charcoal/60">{v.first_seen ? new Date(v.first_seen).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                <td className="py-2.5 text-xs text-dwc-charcoal/60">{v.last_seen ? new Date(v.last_seen).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
              </tr>
            ))}
            {d.visitors.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-dwc-charcoal/50">No visitors recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] font-semibold text-dwc-charcoal/45">Visitors are counted by unique IP address. Trend history builds up automatically from today onward.</p>
    </div>
  );
};

const TrendChart = ({ title, bars, testId }) => {
  const max = Math.max(1, ...bars.map((b) => b.value));
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5" data-testid={testId}>
      <h3 className="text-xs font-extrabold uppercase tracking-wide text-dwc-charcoal/60">{title}</h3>
      <div className="mt-4 flex items-end gap-1.5 sm:gap-2" style={{ height: 140 }}>
        {bars.map((b) => (
          <div key={b.label} className="group flex h-full flex-1 flex-col justify-end text-center" title={b.hint}>
            <p className="mb-1 text-[10px] font-black text-dwc-charcoal/70 opacity-0 transition-opacity group-hover:opacity-100">{b.value}</p>
            <div className="mx-auto w-full max-w-[38px] rounded-t-md bg-dwc-green/80 transition-colors group-hover:bg-dwc-green" style={{ height: `${Math.max(3, (b.value / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex gap-1.5 sm:gap-2">
        {bars.map((b) => (
          <p key={b.label} className="flex-1 truncate text-center text-[8px] font-bold uppercase text-dwc-charcoal/45">{b.label}</p>
        ))}
      </div>
    </div>
  );
};

export const SuccessStoriesAdmin = ({ token }) => {
  const empty = { name: "", location: "", program: "", challenge: "", result: "", image: "", published: true, order: 0 };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/api/store/admin/success-stories`, auth(token)).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFile(file, token);
      setForm((f) => ({ ...f, image: up.url }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error("Upload failed"); }
    setBusy(false);
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, order: Number(form.order) || 0 };
      if (editId) await axios.put(`${API}/api/store/admin/success-stories/${editId}`, body, auth(token));
      else await axios.post(`${API}/api/store/admin/success-stories`, body, auth(token));
      toast.success(editId ? "Story updated" : "Story added");
      setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  const del = async (it) => {
    if (!window.confirm(`Delete story for ${it.name}?`)) return;
    await axios.delete(`${API}/api/store/admin/success-stories/${it.id}`, auth(token));
    toast.success("Story deleted"); load();
  };
  const inputCls = "w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm";
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="stories-admin">
      <form onSubmit={save} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-black uppercase tracking-wide text-dwc-charcoal">{editId ? "Edit Success Story" : "Add Success Story"}</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input required value={form.name} onChange={set("name")} placeholder="Student Name" data-testid="story-name-input" className={inputCls} />
          <input value={form.location} onChange={set("location")} placeholder="Location / City" data-testid="story-location-input" className={inputCls} />
        </div>
        <input value={form.program} onChange={set("program")} placeholder="Training Category (e.g. Confidence Building)" data-testid="story-program-input" className={`${inputCls} mt-3`} />
        <textarea value={form.challenge} onChange={set("challenge")} placeholder="Customer Challenge" rows={2} data-testid="story-challenge-input" className={`${inputCls} mt-3`} />
        <textarea value={form.result} onChange={set("result")} placeholder="Result / Transformation" rows={2} data-testid="story-result-input" className={`${inputCls} mt-3`} />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input type="file" accept="image/*" onChange={onPhoto} data-testid="story-photo-input" className="text-xs" />
          {form.image && <img src={form.image} alt="preview" className="h-12 w-12 rounded-lg object-cover" />}
          <label className="ml-auto inline-flex items-center gap-2 text-xs font-bold text-dwc-charcoal/70">
            <input type="checkbox" checked={form.published} onChange={set("published")} data-testid="story-published-toggle" /> Published
          </label>
          <input type="number" value={form.order} onChange={set("order")} aria-label="Display order" data-testid="story-order-input" className="w-20 rounded-xl border border-gray-200 px-2 py-2 text-sm" />
        </div>
        <div className="mt-4 flex gap-2">
          <button disabled={busy} data-testid="story-save-btn" className="rounded-xl bg-dwc-green px-5 py-2.5 text-xs font-black uppercase tracking-wide text-white">{busy ? "Saving..." : editId ? "Update Story" : "Add Story"}</button>
          {editId && <button type="button" onClick={() => { setEditId(null); setForm(empty); }} className="rounded-xl border border-gray-200 px-4 py-2.5 text-xs font-bold uppercase">Cancel</button>}
        </div>
      </form>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`story-row-${it.id}`} className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            {it.image ? <img src={it.image} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-dwc-soft font-black text-dwc-green">{(it.name || "?")[0]}</span>}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-extrabold text-dwc-charcoal">{it.name} <span className="text-xs font-semibold text-dwc-charcoal/50">· {it.location}</span></p>
              <p className="truncate text-xs text-dwc-charcoal/60">{it.program}</p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase ${it.published ? "bg-dwc-soft text-dwc-green" : "bg-gray-100 text-gray-500"}`}>{it.published ? "Live" : "Hidden"}</span>
            <button onClick={() => { setEditId(it.id); setForm({ ...empty, ...it }); }} aria-label={`Edit ${it.name}`} data-testid={`story-edit-${it.id}`} className="rounded-lg p-2 text-dwc-green"><Pencil className="h-4 w-4" aria-hidden="true" /></button>
            <button onClick={() => del(it)} aria-label={`Delete ${it.name}`} data-testid={`story-delete-${it.id}`} className="rounded-lg p-2 text-dwc-crimson"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-dwc-charcoal/50">No stories yet.</p>}
      </div>
    </div>
  );
};

export const DashboardCounts = ({ token, refreshKey }) => {
  const [c, setC] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/store/admin/dashboard`, auth(token)).then((r) => setC(r.data)).catch(() => {});
  }, [token, refreshKey]);
  if (!c) return null;
  const items = [["Visitors", c.visitors], ["Customers", c.customers], ["LL Users", c.ll_users], ["Orders", c.orders], ["Products", c.products], ["Photos", c.photos], ["Driving Videos", c.driving_videos], ["Review Videos", c.review_videos], ["Exam Modules", c.exam_modules], ["LL Questions", c.ll_questions]];
  return (
    <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-10" data-testid="admin-dashboard-counts">
      {items.map(([label, v]) => (
        <div key={label} className="rounded-xl border border-gray-100 bg-white p-3 text-center">
          <p className="text-xl font-black text-dwc-green">{v}</p>
          <p className="text-[9px] font-extrabold uppercase tracking-wide text-dwc-charcoal/50">{label}</p>
        </div>
      ))}
    </div>
  );
};

export const CustomersAdmin = ({ token }) => {
  const [data, setData] = useState({ leads: [], ll_users: [] });
  const [q, setQ] = useState("");
  useEffect(() => {
    axios.get(`${API}/api/store/admin/customers`, auth(token)).then((r) => setData(r.data)).catch(() => {});
  }, [token]);
  const filtered = data.leads.filter((l) => !q || `${l.name} ${l.mobile} ${l.location}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="mt-6 space-y-4" data-testid="customers-admin-panel">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-dwc-charcoal/40" aria-hidden="true" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers..." data-testid="customers-search" className={`${inputCls} pl-9`} />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead><tr className="border-b border-gray-100 text-[11px] font-extrabold uppercase tracking-wide text-dwc-charcoal/50">
            <th className="px-4 py-3">Name</th><th className="px-4 py-3">Mobile</th><th className="px-4 py-3">DOB / Age</th><th className="px-4 py-3">Location</th><th className="px-4 py-3">Challenge</th><th className="px-4 py-3">Own Car</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Date</th>
          </tr></thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="border-b border-gray-50" data-testid={`customer-row-${l.id}`}>
                <td className="px-4 py-3 font-bold text-dwc-charcoal">{l.name}</td>
                <td className="px-4 py-3">{l.mobile}</td>
                <td className="px-4 py-3 text-xs">{l.dob ? `${l.dob}${l.age ? ` (${l.age})` : ""}` : "—"}</td>
                <td className="px-4 py-3 text-xs">{l.location}</td>
                <td className="px-4 py-3 text-xs">{l.problem}</td>
                <td className="px-4 py-3 text-xs font-bold">{l.own_car || "—"}</td>
                <td className="px-4 py-3 text-xs">{l.type}</td>
                <td className="px-4 py-3 text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN") : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-6 text-center text-sm text-dwc-charcoal/50">No customers found.</p>}
      </div>
      <p className="text-xs font-bold text-dwc-charcoal/50">Learning Licence users: {data.ll_users.length} registered</p>
    </div>
  );
};

export const ModulesAdmin = ({ token }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", number: "", description: "", category: "", version: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => {
    axios.get(`${API}/api/store/admin/modules`, auth(token)).then((r) => setItems(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a PDF file"); return; }
    setBusy(true);
    try {
      const up = await uploadFile(file, token);
      await axios.post(`${API}/api/store/admin/modules`, {
        ...form, number: form.number ? parseInt(form.number, 10) : null, pdf_url: up.url, active: true,
      }, auth(token));
      toast.success("Module published");
      setForm({ name: "", number: "", description: "", category: "", version: "" });
      fileRef.current.value = "";
      load();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed"); }
    setBusy(false);
  };
  const del = async (m) => {
    if (!window.confirm(`Delete module "${m.name}"?`)) return;
    await axios.delete(`${API}/api/store/admin/modules/${m.id}`, auth(token));
    load();
  };
  const toggle = async (m) => {
    await axios.put(`${API}/api/store/admin/modules/${m.id}`, { ...m, active: !m.active }, auth(token));
    load();
  };

  return (
    <div className="mt-6 space-y-4" data-testid="modules-admin-panel">
      <form onSubmit={submit} className="rounded-2xl border border-gray-100 bg-white p-6">
        <h3 className="text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">Upload Exam Module PDF</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input type="file" ref={fileRef} accept="application/pdf" data-testid="module-file-input" className={`${inputCls} sm:col-span-2`} />
          <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Module name *" data-testid="module-name-input" className={inputCls} />
          <input type="number" value={form.number} onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))} placeholder="Module number" className={inputCls} />
          <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder="Category" className={inputCls} />
          <input value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} placeholder="Version" className={inputCls} />
          <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description" className={`${inputCls} sm:col-span-2`} />
        </div>
        <button type="submit" disabled={busy} data-testid="module-upload-btn" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-dwc-green px-5 py-3 text-xs font-bold uppercase tracking-wide text-white disabled:opacity-60">
          <UploadCloud className="h-4 w-4" aria-hidden="true" /> {busy ? "Uploading..." : "Upload Module"}
        </button>
      </form>
      <div className="space-y-2">
        {items.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-100 bg-white p-4" data-testid={`module-row-${m.id}`}>
            <FileText className="h-5 w-5 shrink-0 text-dwc-crimson" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-extrabold text-dwc-charcoal">{m.number ? `${m.number}. ` : ""}{m.name} {m.version && <span className="text-xs text-dwc-charcoal/50">v{m.version}</span>}</p>
              <p className="text-xs text-dwc-charcoal/55">{m.category} {m.description}</p>
            </div>
            <a href={`${API}${m.pdf_url}`} target="_blank" rel="noopener noreferrer" className="text-xs font-bold uppercase text-dwc-green underline">Preview</a>
            <button onClick={() => toggle(m)} className={`rounded-full px-3 py-1 text-[10px] font-black uppercase ${m.active ? "bg-dwc-soft text-dwc-green" : "bg-gray-100 text-gray-500"}`}>{m.active ? "Active" : "Inactive"}</button>
            <button onClick={() => del(m)} aria-label="Delete module" className="rounded p-1.5 text-dwc-crimson hover:bg-dwc-crimson/5"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-2xl bg-white p-6 text-center text-sm text-dwc-charcoal/50">No exam modules uploaded yet.</p>}
      </div>
    </div>
  );
};
